import { Resolve } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppConfig } from '@app/app.config';
import {
  FORM_PERMISSIONS,
  FormPermission,
  IFormPermission
} from '@app/security/shared/form-permission.model';
import { HttpService } from '@app/security/shared/http.service';
import { Injectable } from '@angular/core';
import { Logger } from '@app/logger/logger';
import { LoginStatusModel } from '@app/security/shared/login-status-change.model';
import { LoginStatusService } from '@app/security/shared/login-status.service';
import { first, map, publishReplay, refCount } from 'rxjs/operators';

const lgSfx = '\t(authorization.service)';

@Injectable({ providedIn: 'root' })
export class AuthorizationService implements Resolve<Observable<IFormPermission[]>> {
  private formPermissions = Array<IFormPermission>();
  private loadFormsObserver: Observable<Array<IFormPermission>>;
  private isPermissionsLoaded: boolean;

  public readonly formPermissionsLoadedObserver: Observable<boolean>;
  private formPermissionsLoadedUpdater: BehaviorSubject<boolean>;

  constructor(
    private http: HttpService,
    private config: AppConfig,
    private log: Logger,
    private loginStatusService: LoginStatusService
  ) {
    this.isPermissionsLoaded = false;
    this.formPermissionsLoadedUpdater = new BehaviorSubject<boolean>(this.isPermissionsLoaded);
    this.formPermissionsLoadedObserver = this.formPermissionsLoadedUpdater.asObservable();

    this.loginStatusService.loginStatus.subscribe((loginStatus: LoginStatusModel) => {
      this.onLoginStatusChange(loginStatus);
    });
  }

  private createFormPermission(formId: number, permissionId: number): IFormPermission {
    if (permissionId < 0 || permissionId > 6) {
      this.log.warn(`Trying to create a permission with an invalid permission ID ${lgSfx}`);
      permissionId = FORM_PERMISSIONS.None;
    }

    return new FormPermission(formId, permissionId);
  }

  public getFormPermission(formId: number): IFormPermission {
    const permission = this.formPermissions.find(
      (formPermission) => formPermission.formId === formId
    );

    if (typeof permission === 'undefined' || permission === null) {
      return this.createFormPermission(formId, FORM_PERMISSIONS.None);
    } else {
      return permission;
    }
  }

  public loadFormPermissions(): Observable<Array<IFormPermission>> {
    this.log.log(`Loading user form permissions from the server  ${lgSfx}`);

    const API_ENDPOINT = '/api/formauthorization';
    const endpoint = this.config.getServerUri() + API_ENDPOINT;

    if (this.loadFormsObserver) {
      return this.loadFormsObserver;
    }

    this.loadFormsObserver = this.http.getAny(endpoint).pipe(
      map((data) => {
        // Clear the old permissions and copy in the new permissions.
        this.formPermissions.splice(0, this.formPermissions.length);
        this.formPermissions = data.map((forms) => {
          return this.createFormPermission(forms.FormId, forms.PermissionId);
        });

        this.isPermissionsLoaded = true;
        this.formPermissionsLoadedUpdater.next(this.isPermissionsLoaded);

        return this.formPermissions;
      }),
      publishReplay(1),
      refCount()
    );

    return this.loadFormsObserver;
  }

  private onLoginStatusChange(loginStatus: LoginStatusModel): void {
    if (loginStatus.loggedIn === true) {
      this.loadFormPermissions().pipe(first()).subscribe();
    } else {
      this.cleanCache();
    }
  }

  public resolve(): Observable<IFormPermission[]> {
    return this.loadFormPermissions();
  }

  private cleanCache(): void {
    this.formPermissions = [];
    this.isPermissionsLoaded = false;
    this.loadFormsObserver = undefined;
    this.formPermissionsLoadedUpdater.next(this.isPermissionsLoaded);
  }
}
